import { useMemo, useState } from 'react';
import { usePlanification } from '../../context/PlanificationContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './StatutProjets.css';

const StatutProjets = () => {
  const { planificationData, lastParams } = usePlanification();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fonction d'export PDF
  const exportToPDF = async () => {
    if (!selectedStatus || !selectedStatusProjects) {
      alert('Aucun statut sélectionné pour l\'export');
      return;
    }

    setIsExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);
      const usableHeight = pageHeight - (2 * margin);
      
      let currentY = margin;
      let isFirstPage = true;

      // Fonction pour ajouter l'en-tête sur chaque page
      const addPageHeader = () => {
        const headerHeight = 35;
        
        // Titre principal
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(31, 41, 55);
        pdf.text('Statut des Projets', margin, currentY + 8);
        
        // Période
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(107, 114, 128);
        const periodeText = `Periode: ${lastParams?.dateDebut || 'N/A'} - ${lastParams?.dateFin || 'N/A'}`;
        pdf.text(periodeText, margin, currentY + 15);
        
        // Badge de statut
        const statusColor = getStatusColor(selectedStatus);
        const rgbColor = hexToRgb(statusColor);
        pdf.setFillColor(rgbColor.r, rgbColor.g, rgbColor.b);
        pdf.roundedRect(margin, currentY + 20, 50, 10, 2, 2, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(selectedStatus, margin + 25, currentY + 27, { align: 'center' });
        
        currentY += headerHeight;
        return headerHeight;
      };

      // Fonction pour convertir hex en RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 107, g: 114, b: 128 };
      };

      // Fonction pour calculer la hauteur d'un projet
      const calculateProjectHeight = () => {
        return 45; // Hauteur approximative d'une carte projet en mm
      };

      // Fonction pour calculer la hauteur d'un en-tête de groupe
      const calculateGroupHeaderHeight = () => {
        return 15; // Hauteur de l'en-tête de groupe
      };

      // Fonction pour ajouter un en-tête de groupe
      const addGroupHeader = (groupName, projectCount) => {
        const groupHeaderHeight = calculateGroupHeaderHeight();
        
        // Vérifier si on a assez d'espace pour l'en-tête + au moins un projet
        if (currentY + groupHeaderHeight + calculateProjectHeight() > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
          addPageHeader();
        }
        
        // Fond du groupe
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(margin, currentY, contentWidth, groupHeaderHeight, 2, 2, 'F');
        
        // Icône de dossier stylisée
        pdf.setDrawColor(59, 130, 246);
        pdf.setLineWidth(0.5);
        pdf.setFillColor(59, 130, 246);
        // Corps du dossier
        pdf.roundedRect(margin + 3, currentY + 4, 5, 4, 0.5, 0.5, 'FD');
        // Onglet du dossier
        pdf.rect(margin + 3, currentY + 3, 2, 1, 'F');
        
        // Titre du groupe
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(groupName, margin + 11, currentY + 7);
        
        // Compteur de projets avec fond
        const countText = `${projectCount} projet(s)`;
        const countWidth = pdf.getTextWidth(countText) + 4;
        pdf.setFillColor(226, 232, 240);
        pdf.roundedRect(pageWidth - margin - countWidth - 3, currentY + 3, countWidth, 6, 1, 1, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(100, 116, 139);
        pdf.text(countText, pageWidth - margin - 3, currentY + 7, { align: 'right' });
        
        currentY += groupHeaderHeight + 3;
      };

      // Fonction pour ajouter un projet
      const addProject = (project) => {
        const projectHeight = calculateProjectHeight();
        
        // Vérifier si on a assez d'espace pour le projet complet
        if (currentY + projectHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
          addPageHeader();
        }
        
        const projectY = currentY;
        
        // Bordure de la carte
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(margin, projectY, contentWidth, projectHeight, 2, 2, 'S');
        
        // En-tête du projet (fond bleu)
        const headerColor = hexToRgb('#3b82f6');
        pdf.setFillColor(headerColor.r, headerColor.g, headerColor.b);
        pdf.roundedRect(margin, projectY, contentWidth, 12, 2, 2, 'F');
        
        // Nom du projet
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        const projectName = project.projectName.length > 60 
          ? project.projectName.substring(0, 57) + '...' 
          : project.projectName;
        pdf.text(projectName, margin + 3, projectY + 8);
        
        // Corps de la carte
        let bodyY = projectY + 16;
        
        // Propriétaire avec icône
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        
        // Icône utilisateur (cercle + demi-cercle)
        pdf.setFillColor(100, 116, 139);
        pdf.circle(margin + 4, bodyY - 2, 1.2, 'F'); // Tête
        pdf.ellipse(margin + 4, bodyY + 0.5, 1.5, 1, 'F'); // Corps
        
        pdf.text('Proprietaire:', margin + 8, bodyY);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        const ownerName = project.owner_name.length > 35 
          ? project.owner_name.substring(0, 32) + '...' 
          : project.owner_name;
        pdf.text(ownerName, pageWidth - margin - 3, bodyY, { align: 'right' });
        
        bodyY += 6;
        
        // Groupe avec icône
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        
        // Icône dossier
        pdf.setDrawColor(100, 116, 139);
        pdf.setLineWidth(0.3);
        pdf.setFillColor(100, 116, 139);
        pdf.roundedRect(margin + 3, bodyY - 2.5, 3, 2.5, 0.3, 0.3, 'FD');
        pdf.rect(margin + 3, bodyY - 3, 1.2, 0.5, 'F');
        
        pdf.text('Groupe:', margin + 8, bodyY);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        const groupName = project.group_name.length > 40 
          ? project.group_name.substring(0, 37) + '...' 
          : project.group_name;
        pdf.text(groupName, pageWidth - margin - 3, bodyY, { align: 'right' });
        
        bodyY += 8;
        
        // Ligne de séparation
        pdf.setDrawColor(241, 245, 249);
        pdf.setLineWidth(0.2);
        pdf.line(margin + 3, bodyY, pageWidth - margin - 3, bodyY);
        
        bodyY += 5;
        
        // Progression - Label et valeur
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text('Progression', margin + 3, bodyY);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(`${project.project_percent}%`, pageWidth - margin - 3, bodyY, { align: 'right' });
        
        bodyY += 4;
        
        // Barre de progression
        const progressBarWidth = contentWidth - 6;
        const progressBarHeight = 4;
        
        // Fond de la barre
        pdf.setFillColor(241, 245, 249);
        pdf.roundedRect(margin + 3, bodyY, progressBarWidth, progressBarHeight, 1, 1, 'F');
        
        // Remplissage de la barre
        const progressPercent = Math.min(parseFloat(project.project_percent), 100);
        const progressWidth = (progressBarWidth * progressPercent) / 100;
        
        if (progressWidth > 0) {
          const progressColor = hexToRgb(getProgressColor(project.project_percent));
          pdf.setFillColor(progressColor.r, progressColor.g, progressColor.b);
          pdf.roundedRect(margin + 3, bodyY, progressWidth, progressBarHeight, 1, 1, 'F');
        }
        
        currentY += projectHeight + 3;
      };

      // Ajouter l'en-tête de la première page
      addPageHeader();

      // Parcourir tous les groupes et projets
      for (const [groupName, projects] of Object.entries(selectedStatusProjects)) {
        // Ajouter l'en-tête du groupe
        addGroupHeader(groupName, projects.length);
        
        // Ajouter chaque projet
        for (const project of projects) {
          addProject(project);
        }
        
        // Ajouter un petit espace entre les groupes
        currentY += 5;
      }
      
      // Sauvegarder le PDF
      const fileName = `Statut_Projets_${selectedStatus.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Extraire les projets uniques avec leurs informations
  const projectsData = useMemo(() => {
    if (!planificationData?.hierarchie) return [];

    const projectsMap = new Map(); // Map<projectName, {status, owner_name, group_name, project_percent}>

    // Parcourir tous les groupes
    Object.values(planificationData.hierarchie).forEach((groupe) => {
      // Parcourir tous les projets
      Object.entries(groupe).forEach(([projectName, projectGroup]) => {
        // Normaliser le nom du projet pour éviter les doublons
        const normalizedProjectName = projectName.trim();

        // Exclure le projet "Tâches Divers"
        if (normalizedProjectName.toLowerCase() === 'tâches divers') {
          return; // Skip ce projet
        }

        // Ajouter le projet s'il n'existe pas déjà
        if (!projectsMap.has(normalizedProjectName)) {
          projectsMap.set(normalizedProjectName, {
            projectName: normalizedProjectName,
            status: projectGroup.status || 'N/A',
            owner_name: projectGroup.owner_name || 'N/A',
            group_name: projectGroup.group_name || 'N/A',
            project_percent: projectGroup.project_percent || '0'
          });
        }
      });
    });

    // Convertir la Map en tableau
    return Array.from(projectsMap.values());
  }, [planificationData]);

  // Grouper les projets par statut puis par group_name
  const groupedProjects = useMemo(() => {
    const grouped = {};

    projectsData.forEach((project) => {
      const status = project.status;
      const groupName = project.group_name;

      if (!grouped[status]) {
        grouped[status] = {};
      }

      if (!grouped[status][groupName]) {
        grouped[status][groupName] = [];
      }

      grouped[status][groupName].push(project);
    });

    // Trier les projets dans chaque groupe alphabétiquement
    Object.keys(grouped).forEach((status) => {
      Object.keys(grouped[status]).forEach((groupName) => {
        grouped[status][groupName].sort((a, b) =>
          a.projectName.localeCompare(b.projectName)
        );
      });
    });

    return grouped;
  }, [projectsData]);

  // Liste des statuts disponibles
  const availableStatuses = useMemo(() => {
    return Object.keys(groupedProjects);
  }, [groupedProjects]);

  // Sélectionner automatiquement le premier statut
  useMemo(() => {
    if (availableStatuses.length > 0 && !selectedStatus) {
      setSelectedStatus(availableStatuses[0]);
    }
  }, [availableStatuses, selectedStatus]);

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('cloturé')) return '#10b981'; // Vert
    if (statusLower.includes('en cours')) return '#3b82f6'; // Bleu
    if (statusLower.includes('planification')) return '#f59e0b'; // Orange
    if (statusLower.includes('terminé')) return '#10b981'; // Vert
    if (statusLower.includes('attente')) return '#8b5cf6'; // Violet
    if (statusLower.includes('bloqué')) return '#ef4444'; // Rouge
    return '#6b7280'; // Gris par défaut
  };

  // Obtenir la couleur de la barre de progression
  const getProgressColor = (percent) => {
    const value = parseFloat(percent);
    if (value >= 75) return '#10b981'; // Vert
    if (value >= 50) return '#3b82f6'; // Bleu
    if (value >= 25) return '#f59e0b'; // Orange
    return '#ef4444'; // Rouge
  };

  if (!planificationData) {
    return (
      <div className="statut-projets-container">
        <div className="statut-projets-empty">
          <p>Aucune donnée de planification disponible.</p>
          <p className="statut-projets-hint">
            Veuillez d'abord charger les données depuis la page Planification.
          </p>
        </div>
      </div>
    );
  }

  // Obtenir les projets du statut sélectionné
  const selectedStatusProjects = selectedStatus ? groupedProjects[selectedStatus] : {};
  const selectedStatusProjectCount = selectedStatus 
    ? Object.values(selectedStatusProjects).reduce((sum, projects) => sum + projects.length, 0)
    : 0;

  return (
    <div className="statut-projets-container">
      {/* Header */}
      <div className="statut-projets-header">
        <div className="statut-projets-title-section">
          <h1 className="statut-projets-title">📊 Statut des Projets</h1>
          {lastParams && (
            <p className="statut-projets-period">
              Période: {lastParams.dateDebut} → {lastParams.dateFin}
            </p>
          )}
        </div>
        <div className="statut-projets-header-actions">
          <div className="statut-projets-stats">
            <div className="statut-projets-stat-card">
              <span className="statut-projets-stat-label">Total Projets</span>
              <span className="statut-projets-stat-value">{projectsData.length}</span>
            </div>
            <div className="statut-projets-stat-card">
              <span className="statut-projets-stat-label">Statuts</span>
              <span className="statut-projets-stat-value">{availableStatuses.length}</span>
            </div>
          </div>
          <button 
            className="statut-projets-export-btn"
            onClick={exportToPDF}
            disabled={!selectedStatus || isExporting}
            title="Exporter le statut sélectionné en PDF"
          >
            {isExporting ? (
              <>
                <span className="export-spinner"></span>
                Export en cours...
              </>
            ) : (
              <>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Exporter PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs des statuts */}
      {availableStatuses.length > 0 && (
        <div className="statut-projets-tabs-container">
          <div className="statut-projets-tabs">
            {availableStatuses.map((status) => {
              const projectCount = Object.values(groupedProjects[status]).reduce(
                (sum, projects) => sum + projects.length, 
                0
              );
              const isActive = selectedStatus === status;
              
              return (
                <button
                  key={status}
                  className={`statut-projets-tab ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedStatus(status)}
                  style={{
                    borderBottomColor: isActive ? getStatusColor(status) : 'transparent'
                  }}
                >
                  <span
                    className="statut-projets-tab-badge"
                    style={{ backgroundColor: getStatusColor(status) }}
                  />
                  <span className="statut-projets-tab-label">{status}</span>
                  <span className="statut-projets-tab-count">{projectCount}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Contenu du statut sélectionné */}
      <div className="statut-projets-content">
        {!selectedStatus || Object.keys(selectedStatusProjects).length === 0 ? (
          <div className="statut-projets-empty">
            <p>Aucun projet trouvé.</p>
          </div>
        ) : (
          <div className="statut-projets-status-content">
            {/* Info du statut sélectionné */}
            <div className="statut-projets-status-info">
              <div className="statut-projets-status-info-header">
                <h2 className="statut-projets-status-info-title">
                  <span
                    className="statut-projets-status-info-badge"
                    style={{ backgroundColor: getStatusColor(selectedStatus) }}
                  >
                    {selectedStatus}
                  </span>
                </h2>
                <span className="statut-projets-status-info-count">
                  {selectedStatusProjectCount} projet(s)
                </span>
              </div>
            </div>

            {/* Groupes par group_name */}
            <div className="statut-projets-groups">
              {Object.entries(selectedStatusProjects).map(([groupName, projects]) => (
                <div key={groupName} className="statut-projets-group">
                  <div className="statut-projets-group-header">
                    <h3 className="statut-projets-group-title">
                      📁 {groupName}
                    </h3>
                    <span className="statut-projets-group-count">
                      {projects.length} projet(s)
                    </span>
                  </div>

                  {/* Liste des projets */}
                  <div className="statut-projets-list">
                    {projects.map((project, index) => (
                      <div key={index} className="statut-projets-card">
                        {/* Nom du projet */}
                        <div className="statut-projets-card-header">
                          <h4 className="statut-projets-card-title">
                            {project.projectName}
                          </h4>
                        </div>

                        {/* Informations du projet */}
                        <div className="statut-projets-card-body">
                          <div className="statut-projets-info-row">
                            <span className="statut-projets-info-label">
                              👤 Propriétaire:
                            </span>
                            <span className="statut-projets-info-value">
                              {project.owner_name}
                            </span>
                          </div>

                          <div className="statut-projets-info-row">
                            <span className="statut-projets-info-label">
                              📁 Groupe:
                            </span>
                            <span className="statut-projets-info-value">
                              {project.group_name}
                            </span>
                          </div>

                          {/* Barre de progression */}
                          <div className="statut-projets-progress-section">
                            <div className="statut-projets-progress-header">
                              <span className="statut-projets-progress-label">
                                Progression
                              </span>
                              <span className="statut-projets-progress-value">
                                {project.project_percent}%
                              </span>
                            </div>
                            <div className="statut-projets-progress-bar">
                              <div
                                className="statut-projets-progress-fill"
                                style={{
                                  width: `${Math.min(parseFloat(project.project_percent), 100)}%`,
                                  backgroundColor: getProgressColor(project.project_percent)
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatutProjets;
